import {
  formatCategoryNames,
  isItemInQuantityPromotion,
  quantityPromotionLabel,
  type QuantityPromotionRule,
} from '@/shared/utils/quantity-promotion';

import type { GetStoreItemsResponse, StoreItem } from '../types/get-store-items';

/**
 * Escopo da promoção por quantidade no catálogo (PRAT-184): a regra guarda só
 * `category_ids`; o nome da categoria vem do cardápio em memória, que chega
 * agrupado por nome (`items` = { "Hambúrguers": [...] }) com `category_id` em
 * cada produto. É isso que deixa claro "vale para Hambúrguers" e não para
 * "Lanches e Combos".
 */

type Catalog = Pick<GetStoreItemsResponse, 'items' | 'categories'> | null | undefined;

/** `category_id` → nome do grupo do cardápio em que o produto está. */
export type CategoryNameById = Record<string, string>;

/**
 * Índice `category_id` → nome da categoria. Grupos virtuais (Mais pedidos,
 * Promoções...) misturam produtos de várias categorias, então entram só como
 * último recurso, quando nenhum grupo real tem a categoria.
 */
export function buildCategoryNameById(catalog: Catalog): CategoryNameById {
  const index: CategoryNameById = {};
  if (!catalog?.items) return index;

  const virtual = new Set(
    (catalog.categories ?? [])
      .filter(meta => meta?.is_virtual === true && typeof meta.name === 'string')
      .map(meta => meta.name)
  );

  const fallback: CategoryNameById = {};
  for (const [name, group] of Object.entries(catalog.items)) {
    const target = virtual.has(name) ? fallback : index;
    for (const product of (group ?? []) as StoreItem[]) {
      const categoryId = product?.category_id;
      if (!categoryId || target[categoryId]) continue;
      target[categoryId] = name;
    }
  }

  for (const [categoryId, name] of Object.entries(fallback)) {
    if (!index[categoryId]) index[categoryId] = name;
  }
  return index;
}

/**
 * Nomes das categorias que participam, na ordem de `category_ids`. Categoria
 * sem produto no cardápio (ou sem nome resolvido) é pulada.
 */
export function participatingCategoryNames(
  rule: QuantityPromotionRule | null | undefined,
  categoryNameById: CategoryNameById
): string[] {
  const ids = Array.isArray(rule?.category_ids) ? rule.category_ids : [];
  const names: string[] = [];
  for (const id of ids) {
    const name = categoryNameById[id];
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

/**
 * Esta categoria do cardápio participa? Grupo real cujo `category_id` (o dos
 * produtos dele) está em `category_ids`; lista vazia = todas. Grupo virtual
 * nunca ganha o chip no cabeçalho — os produtos dele já mostram o selo.
 */
export function isCategoryInQuantityPromotion(
  rule: QuantityPromotionRule | null | undefined,
  category: { items: StoreItem[]; isVirtual?: boolean }
): boolean {
  if (!rule || category.isVirtual) return false;
  const ids = Array.isArray(rule.category_ids) ? rule.category_ids : [];
  const sample = category.items.find(item => item?.category_id);
  if (!sample) return false;
  if (ids.length === 0) return true;
  return ids.includes(sample.category_id ?? '');
}

/**
 * Linha da página/drawer do produto: "Promoção: 2º item com 50% — vale para
 * Hambúrguers". Null quando o produto não participa.
 */
export function quantityPromotionProductNote(
  rule: QuantityPromotionRule | null | undefined,
  product: Pick<StoreItem, 'id' | 'category_id'> | null | undefined,
  categoryNameById: CategoryNameById
): string | null {
  if (!rule || !product || !isItemInQuantityPromotion(rule, product)) return null;
  const ids = Array.isArray(rule.category_ids) ? rule.category_ids : [];
  const label = `Promoção: ${quantityPromotionLabel(rule)}`;
  if (ids.length === 0) return `${label} — vale para todo o cardápio`;
  const names = formatCategoryNames(participatingCategoryNames(rule, categoryNameById));
  return names ? `${label} — vale para ${names}` : label;
}
